import isEmpty from 'lodash/isEmpty';
import { Navigate } from 'react-router-dom';

import { root } from '~/preloaded';
import settings from '~/settings';
import {
  CLAN_BATTLES_TABS,
  CLAN_WARS_TABS,
  PATROLS_TABS,
  REQUESTS_TABS,
  sawClanStars,
  sawPatrols,
  TREASURY_TABS,
} from '~/constants';
import { shouldHideTasksNav } from '~/helpers/clanTasks';
import { clanstarsIsEnabled, clanstarsSeasonVersion, darwinIsEnabled, isPatrolsEnabled } from '~/helpers/common';
import { getLastBrawlSeason, getLastRegularSeason } from '~/helpers/ladder';
import { getValueFromLocalStorage, setValueToLocalStorage } from '~/helpers/localStorage';
import { t } from '~/helpers/localization';
import { lastSeason } from '~/helpers/seasons';
import { hasPermission, PERMISSIONS } from '~/roles';

import About from '~/pages/About/About';
import ClanBattles from '~/pages/ClanBattles/ClanBattles';
import ClanBattlesRulesPage from '~/pages/ClanBattles/ClanBattlesRulesPage/ClanBattlesRulesPage';
import ClanBattlesWelcome from '~/pages/ClanBattles/ClanBattlesWelcome/ClanBattlesWelcome';
import ClanBattlesRatingTab from '~/pages/ClanBattles/ClanWarsRating/ClanWarsRating';
import ClansBattlesHallFame from '~/pages/ClanBattles/ClansBattlesHallFame/ClansBattlesHallFame';
import ClanCreate from '~/pages/ClanCreate/ClanCreate';
import ClanMembers from '~/pages/ClanMembers/ClanMembers';
import ClanProfile from '~/pages/ClanProfile/ClanProfile';
import ClanStars from '~/pages/ClanStars/ClanStars';
import ClanTasks from '~/pages/ClanTasks/ClanTasks';
import { CLAN_TASK_NAMES } from '~/pages/ClanTasks/constants';
import Recommendations from '~/pages/Recommendations/Recommendations';
import Requests from '~/pages/Requests/Requests';
import RequestsApplications from '~/pages/Requests/RequestsApplications';
import RequestsInvites from '~/pages/Requests/RequestsInvites';
import Search from '~/pages/Search/Search';

import { backgroundImages, backgroundVideos } from './backgrounds';
import RootRedirect from './guards/RootRedirect';
import LazyRoutesBoundary from './lazy/LazyRoutesBoundary/LazyRoutesBoundary';
import { LazyPages } from './lazy/lazyPages';
import { ROUTES_BADGES, ROUTES_MAP, ROUTES_SECTIONS } from './routes';

import type { RouteObject } from 'react-router-dom';

const routes: RouteObject[] = [
  {
    index: true,
    handle: {
      bgImage: backgroundImages.clanHub,
      bgClassName: 'clanHub',
      display: () => false,
    },
    Component: RootRedirect,
  },
  {
    path: ROUTES_MAP.ABOUT,
    Component: About,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_MANAGEMENT, ROUTES_SECTIONS.ABOUT_CLANS],
      sectionOrder: 100,
      title: () => t('About Clans'),
      display: ({ currentAccount }) => !currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (currentAccount.clanId) {
          return ROUTES_MAP.SEARCH;
        }
      },
    },
  },
  {
    path: ROUTES_MAP.PROFILE,
    Component: ClanProfile,
    handle: {
      badge: ROUTES_BADGES.PROFILE,
      title: () => t('Clan Base'),
      display: ({ currentAccount }) => !!currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.clanId) {
          return ROUTES_MAP.SEARCH;
        }
      },
    },
  },
  {
    path: ROUTES_MAP.TASKS,
    Component: ClanTasks,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_MANAGEMENT],
      sectionOrder: 300,
      badge: ROUTES_BADGES.TASKS,
      title: () => t('Missions'),
      bgImage: backgroundImages.tasks,
      display: ({ currentAccount, clanTasks }) => {
        return darwinIsEnabled && !!currentAccount.clanId && !shouldHideTasksNav(clanTasks, CLAN_TASK_NAMES.length);
      },
      navigationGuard: ({ currentAccount, clanTasks }) => {
        const sawClanTasks = getValueFromLocalStorage<Record<string, boolean>>('sawClanTasks');
        if (!darwinIsEnabled || !currentAccount.clanId || shouldHideTasksNav(clanTasks, CLAN_TASK_NAMES.length)) {
          return ROUTES_MAP.ROOT;
        }
        if (currentAccount.id && !sawClanTasks[currentAccount.id]) {
          sawClanTasks[currentAccount.id] = true;
          setValueToLocalStorage('sawClanTasks', sawClanTasks);
        }
      },
    },
  },
  {
    path: ROUTES_MAP.CLAN_PROFILE,
    Component: ClanProfile,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_EXTERNAL],
      sectionOrder: 100,
      title: () => t('Clan Base'),
      display: () => true,
      end: true,
      replace: true,
    },
  },
  {
    path: ROUTES_MAP.CLAN_PROFILE_MEMBERS,
    Component: ClanMembers,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_EXTERNAL],
      sectionOrder: 200,
      bgImage: backgroundImages.members,
      title: () => t('Clan Members'),
      display: () => true,
      end: true,
      replace: true,
    },
  },
  {
    path: ROUTES_MAP.MEMBERS,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_MANAGEMENT],
      sectionOrder: 100,
      badge: ROUTES_BADGES.MEMBERS,
      title: () => t('Clan Members'),
      bgImage: backgroundImages.members,
      display: ({ currentAccount }) => !!currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.clanId) {
          return ROUTES_MAP.ROOT;
        }
      },
    },
    Component: ClanMembers,
  },
  {
    path: ROUTES_MAP.TREASURY,
    element: (
      <LazyRoutesBoundary>
        <LazyPages.Treasury.Index />
      </LazyRoutesBoundary>
    ),
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_MANAGEMENT],
      sectionOrder: 200,
      title: () => t('Treasury'),
      bgClassName: 'fade60',
      bgImage: backgroundImages.treasury,
      display: ({ currentAccount }) => !!currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.clanId) {
          return ROUTES_MAP.ROOT;
        }
      },
    },
    children: [
      {
        index: true,
        element: <Navigate to={`${root}${ROUTES_MAP.TREASURY_EXPENSES}`} replace={true} />,
      },
      {
        path: TREASURY_TABS.EXPENSES,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Treasury.History />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Казна клана - Расходы'),
          display: () => false,
        },
      },
      {
        path: TREASURY_TABS.INCOME,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Treasury.History />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Казна клана - Доходы'),
          display: () => false,
        },
      },
      {
        path: TREASURY_TABS.DISTRIBUTION,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Treasury.Distribution />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Казна клана - Распределение'),
          display: () => false,
        },
      },
      {
        path: TREASURY_TABS.REWARDS,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Treasury.Rewards />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Казна клана - Регулярные награды'),
          display: () => false,
        },
      },
    ],
  },
  {
    path: ROUTES_MAP.REQUESTS,
    Component: Requests,
    handle: {
      sections: [ROUTES_SECTIONS.CLAN_MANAGEMENT, ROUTES_SECTIONS.ABOUT_CLANS],
      sectionOrder: 400,
      badge: ROUTES_BADGES.REQUESTS,
      title: () => t('Запросы'),
      bgImage: backgroundImages.members,
      display: ({ currentAccount }) => {
        return (
          (currentAccount.id && !currentAccount.clanId) ||
          Boolean(currentAccount.roleName && hasPermission(currentAccount.roleName, PERMISSIONS.HANDLE_INVITES))
        );
      },
      navigationGuard: ({ currentAccount }) => {
        if (
          !currentAccount.id ||
          (currentAccount.clanId &&
            currentAccount.roleName &&
            !hasPermission(currentAccount.roleName, PERMISSIONS.HANDLE_INVITES))
        ) {
          return ROUTES_MAP.PROFILE;
        }
      },
    },
    children: [
      {
        index: true,
        element: <Navigate to={`${root}${ROUTES_MAP.REQUESTS_APPLICATIONS}`} replace={true} />,
        handle: {
          navigationGuard: ({ currentAccount }) => {
            if (!currentAccount.clanId) {
              return ROUTES_MAP.REQUESTS_INVITES;
            }
          },
        },
      },
      {
        path: REQUESTS_TABS.APPLICATION,
        Component: RequestsApplications,
        handle: {
          display: () => false,
        },
      },
      {
        path: REQUESTS_TABS.INVITES,
        Component: RequestsInvites,
        handle: {
          display: () => false,
        },
      },
    ],
  },
  {
    path: ROUTES_MAP.CLAN_CREATE,
    Component: ClanCreate,
    handle: {
      badge: ROUTES_BADGES.CREATE,
      title: () => t('Create a Clan'),
      bgClassName: 'fade60',
      bgImage: backgroundImages.create,
      display: ({ currentAccount }) => !!currentAccount.id && !currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (currentAccount.clanId) {
          return ROUTES_MAP.PROFILE;
        }
      },
    },
  },
  {
    path: ROUTES_MAP.SEARCH,
    Component: Search,
    handle: {
      sections: [ROUTES_SECTIONS.ABOUT_CLANS, ROUTES_SECTIONS.CLAN_MANAGEMENT],
      sectionOrder: ({ currentAccount }) => {
        if (currentAccount.clanId) {
          return 500;
        }
        return 350;
      },
      badge: ROUTES_BADGES.SEARCH,
      title: ({ currentAccount }) => {
        if (!currentAccount?.clanId) {
          return t('Find a Clan');
        }
        return t('Explore Clans');
      },
      bgImage: backgroundImages.search,
    },
  },
  {
    path: ROUTES_MAP.SEARCH_RECOMMENDATIONS,
    Component: Recommendations,
    handle: {
      badge: ROUTES_BADGES.RECOMMENDATIONS_NEW,
      title: () => t('Рекомендации'),
      bgImage: backgroundImages.search,
      display: () => false,
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.id || (currentAccount.clanId && !settings.clanrec2?.showRecommendationsForClansMembers)) {
          return ROUTES_MAP.ROOT;
        }
      },
    },
  },
  {
    path: ROUTES_MAP.PATROLS,
    element: (
      <LazyRoutesBoundary>
        <LazyPages.Patrols.Index />
      </LazyRoutesBoundary>
    ),
    handle: {
      badge: ROUTES_BADGES.PATROLS,
      bgImage: backgroundImages.patrols,
      display: ({ currentAccount }) => isPatrolsEnabled() && !!currentAccount.clanId,
      separator: () => true,
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.clanId || !isPatrolsEnabled()) {
          return ROUTES_MAP.ROOT;
        }
        if (currentAccount.id && !sawPatrols[currentAccount.id]) {
          sawPatrols[currentAccount.id] = true;
          const keyLocalStorage = `sawPatrols_${settings.patrols.eventFinishesAt}`;
          setValueToLocalStorage(keyLocalStorage, sawPatrols);
        }
      },
      title: () => t('Clan Patrols'),
    },
    children: [
      {
        index: true,
        element: <Navigate to={`${root}${ROUTES_MAP.PATROLS_ACTIVE}`} replace={true} />,
        handle: {
          title: () => t('Clan Patrols'),
        },
      },
      {
        path: PATROLS_TABS.ACTIVE,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Patrols.Active />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Clan Patrols'),
          display: () => false,
        },
      },
      {
        path: PATROLS_TABS.LIST,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Patrols.List />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Clan Patrols'),
          display: () => false,
        },
      },
      {
        path: PATROLS_TABS.RULES,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.Patrols.Rules />
          </LazyRoutesBoundary>
        ),
        handle: {
          title: () => t('Clan Patrols'),
          display: () => false,
        },
      },
    ],
  },
  {
    path: ROUTES_MAP.CLAN_WARS,
    element: (
      <LazyRoutesBoundary>
        <LazyPages.ClanWars.Index />
      </LazyRoutesBoundary>
    ),
    handle: {
      badge: ROUTES_BADGES.CLAN_WARS,
      title: () => t('Морское сражение'),
      bgClassName: 'fade25',
      bgImage: backgroundImages.clanWars,
      separator: ({ currentAccount }) => !(isPatrolsEnabled() && !!currentAccount.clanId),
      display: ({ currentAccount }) => !!currentAccount.id && settings.cwars.isEnabled,
      navigationGuard: () => {
        if (!settings.cwars.isEnabled) {
          return ROUTES_MAP.SEARCH;
        }
      },
    },
    children: [
      {
        index: true,
        element: null,
        handle: {
          navigationGuard: ({ currentAccount, clanWarsIsParticipating }) => {
            if (currentAccount.clanId && clanWarsIsParticipating) {
              return ROUTES_MAP.CLAN_WARS_WAR;
            }
            return ROUTES_MAP.CLAN_WARS_WELCOME;
          },
        },
      },
      {
        path: CLAN_WARS_TABS.WAR,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.ClanWars.Tabs.War />
          </LazyRoutesBoundary>
        ),
        handle: {
          badge: ROUTES_BADGES.CLAN_WARS,
          title: () => t('Морское сражение - Война'),
          bgClassName: 'fade25',
          bgImage: backgroundImages.clanWars,
          display: () => false,
          navigationGuard: ({ currentAccount, clanWarsIsParticipating }) => {
            if (!currentAccount.clanId || !clanWarsIsParticipating || !currentAccount.id) {
              return ROUTES_MAP.CLAN_WARS_WELCOME;
            }
          },
        },
      },
      {
        path: CLAN_WARS_TABS.WELCOME,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.ClanWars.Tabs.Welcome />
          </LazyRoutesBoundary>
        ),
        handle: {
          badge: ROUTES_BADGES.CLAN_WARS,
          title: () => t('Морское сражение - Война'),
          display: () => false,
          navigationGuard: ({ currentAccount, clanWarsIsParticipating }) => {
            if (currentAccount.clanId && clanWarsIsParticipating) {
              return ROUTES_MAP.CLAN_WARS_WAR;
            }
          },
        },
      },
      {
        path: CLAN_WARS_TABS.RULES,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.ClanWars.Tabs.Rules />
          </LazyRoutesBoundary>
        ),
        handle: {
          badge: ROUTES_BADGES.CLAN_WARS,
          title: () => t('Морское сражение - Правила'),
          bgClassName: 'fade100',
          display: () => false,
        },
      },
      {
        path: CLAN_WARS_TABS.RATING,
        element: (
          <LazyRoutesBoundary>
            <LazyPages.ClanWars.Tabs.Rating />
          </LazyRoutesBoundary>
        ),
        handle: {
          badge: ROUTES_BADGES.CLAN_WARS,
          title: () => t('Морское сражение - Рейтинг'),
          bgClassName: 'fade100',
          display: () => false,
          navigationGuard: ({ currentAccount }) => {
            if (!currentAccount.id) {
              return ROUTES_MAP.CLAN_WARS_WELCOME;
            }
          },
        },
      },
    ],
  },
  {
    path: ROUTES_MAP.CLAN_BATTLES,
    Component: ClanBattles,
    handle: {
      badge: ROUTES_BADGES.CLAN_BATTLES,
      title: () => t('Клановые бои'),
      bgImage: backgroundImages.clanBattles,
      display: () => {
        return !!(getLastRegularSeason() || getLastBrawlSeason());
      },
      navigationGuard: ({ currentAccount }) => {
        if (!currentAccount.clanId && isEmpty(lastSeason)) {
          return ROUTES_MAP.ROOT;
        }
      },
    },
    children: [
      {
        index: true,
        element: <Navigate to={`${root}${ROUTES_MAP.CLAN_BATTLES_HISTORY}`} replace />,
      },
      {
        path: CLAN_BATTLES_TABS.HISTORY,
        Component: ClanBattlesWelcome,
        handle: {
          badge: ROUTES_BADGES.CLAN_BATTLES,
          bgClassName: 'fade50',
          title: () => t('Клановые бои - История'),
          display: () => false,
        },
      },
      {
        path: CLAN_BATTLES_TABS.RATING,
        Component: ClanBattlesRatingTab,
        handle: {
          badge: ROUTES_BADGES.CLAN_BATTLES,
          title: () => t('Клановые бои - Рейтинги'),
          display: () => false,
        },
        children: [
          {
            path: ':seasonType/:season/:realm',
            Component: ClanBattlesRatingTab,
          },
        ],
      },
      {
        path: CLAN_BATTLES_TABS.HALL,
        Component: ClansBattlesHallFame,
        handle: {
          badge: ROUTES_BADGES.CLAN_BATTLES,
          bgImage: backgroundImages.hallOfFame,
          bgVideo: backgroundVideos.hallOfFame,
          bgClassName: 'hallOfFame',
          title: () => t('Клановые бои - Зал славы'),
          display: () => false,
          navigationGuard: () => {
            if (!settings.ladder.hallOfFameIsEnabled) {
              return ROUTES_MAP.ROOT;
            }
          },
        },
      },
      {
        path: CLAN_BATTLES_TABS.RULES,
        Component: ClanBattlesRulesPage,
        handle: {
          badge: ROUTES_BADGES.CLAN_BATTLES,
          title: () => t('Клановые бои - Правила'),
          display: () => false,
        },
      },
    ],
  },
  {
    path: ROUTES_MAP.CLAN_STARS,
    Component: ClanStars,
    handle: {
      badge: ROUTES_BADGES.CLAN_STARS,
      bgImage: backgroundImages.clanStars,
      display: ({ currentAccount }) => clanstarsIsEnabled && !!currentAccount.clanId,
      navigationGuard: ({ currentAccount }) => {
        if (!clanstarsIsEnabled || !currentAccount.clanId) {
          return ROUTES_MAP.ROOT;
        }
        if (currentAccount.id && !sawClanStars[currentAccount.id]) {
          sawClanStars[currentAccount.id] = true;
          const keyLocalStorage = `sawClanStars_${clanstarsSeasonVersion}`;
          setValueToLocalStorage(keyLocalStorage, sawClanStars);
        }
      },
      title: () => t('Звезда отряда'),
    },
  },
];

export default routes;
